import React from 'react';

import { css, cx } from '@emotion/css';
import { Theme } from '@mui/material';

import { useStyles } from '@/hooks/useStyles';
import { PaletteColors } from '@/theme/PaletteOverrides.ts';
import { AtomProps } from '@/uiKit/atoms/utils/AtomTypes.ts';

type EmptyDotSize = 'caption' | 'body';

export type BaseDotProps = {
  color: PaletteColors;
  border?: boolean;
  content?: React.ReactNode;
  size?: EmptyDotSize;
};

export type DotProps = AtomProps<BaseDotProps>;

/**
 * A functional React component that renders a stylized dot.
 *
 * Either just a simple circle or a circle with content inside.
 *
 */
export const Dot: React.FC<DotProps> = ({
  color,
  border = false,
  size = 'body',
  content,
  className,
  ...props
}) => {
  const styles = useStyles(
    makeStyles,
    color,
    size,
    border,
    content !== undefined,
  );

  return (
    <div className={cx(styles.item, className)} {...props}>
      {content}
    </div>
  );
};

const makeStyles = (
  theme: Theme,
  color: PaletteColors,
  size: EmptyDotSize,
  border: boolean,
  hasContent: boolean,
) => ({
  item: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: hasContent ? 'fit-content' : theme.typography[size].fontSize,
    minHeight: 0,
    height: hasContent ? 'fit-content' : theme.typography[size].fontSize,
    padding: theme.spacing(3),
    aspectRatio: 1,
    borderRadius: '100%',
    border: border
      ? `${theme.spacing(0.5)} solid ${theme.palette[color].main}`
      : '',
    backgroundColor: theme.palette[color].contrastText,
    color: theme.palette[color].dark,
  }),
});
